import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';
import { collapseInOut } from '../../animations';
import { SmoothCollapseDirective } from '../../directives/smooth-collapse.directive';
import { UmssCheckboxComponent } from '../checkbox/checkbox.component';
import { UmssHorizontalFieldComponent } from '../horizontal-field/horizontal-field.component';
import { UmssInputComponent } from '../input/input.component';
import { getErrorsPerControl } from '@umss/shared/utils';
import { debounceTime } from 'rxjs';

/** Locked contract: the FormGroup passed to this component must have exactly these controls. */
export interface TimeRangeFormGroup {
  allDay: AbstractControl<boolean | null>;
  startTime: AbstractControl<string | null>;
  endTime: AbstractControl<string | null>;
}

/**
 * Internal helper that adds Angular's FormGroup<T> index-signature constraint
 * without polluting the public TimeRangeFormGroup interface.
 */
type AngularFormControls<T> = { [K in keyof T]: AbstractControl<unknown> };

@Component({
  selector: 'umss-time-range-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    UmssHorizontalFieldComponent,
    UmssInputComponent,
    UmssCheckboxComponent,
    SmoothCollapseDirective
  ],
  templateUrl: './time-range-field.component.html',
  styleUrl: './time-range-field.component.scss',
  animations: [collapseInOut]
})
export class UmssTimeRangeFieldComponent<
  T extends TimeRangeFormGroup & AngularFormControls<T>
> implements OnInit {
  formGroup = input.required<FormGroup<T>>();

  startTimeErrors = signal<Set<string> | null>(null);
  endTimeErrors = signal<Set<string> | null>(null);

  private destroyRef = inject(DestroyRef);

  /** Typed accessor — safe because T extends TimeRangeFormGroup */
  private get c(): TimeRangeFormGroup {
    return this.formGroup().controls as unknown as TimeRangeFormGroup;
  }

  ngOnInit(): void {
    this.formGroup()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300))
      .subscribe(() => this.checkErrors());

    this.c.allDay.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allDay) => this.onAllDayChange(allDay));

    this.onAllDayChange(this.c.allDay.value);
  }

  private onAllDayChange(allDay: boolean | null): void {
    if (allDay) {
      this.c.startTime.setValue('00:00', { emitEvent: false });
      this.c.endTime.setValue('23:59', { emitEvent: false });
      this.c.startTime.disable({ emitEvent: false });
      this.c.endTime.disable({ emitEvent: false });
    } else {
      this.c.startTime.enable({ emitEvent: false });
      this.c.endTime.enable({ emitEvent: false });
      this.c.startTime.setValue(null, { emitEvent: false });
      this.c.endTime.setValue(null, { emitEvent: false });
      this.c.startTime.markAsPristine();
      this.c.startTime.markAsUntouched();
      this.c.endTime.markAsPristine();
      this.c.endTime.markAsUntouched();
    }
  }

  showStartError(): boolean {
    const ctrl = this.c.startTime;
    return (ctrl.dirty || ctrl.touched) && !!this.startTimeErrors()?.size;
  }

  showEndError(): boolean {
    const ctrl = this.c.endTime;
    return (ctrl.dirty || ctrl.touched) && !!this.endTimeErrors()?.size;
  }

  private checkErrors(): void {
    const errors = getErrorsPerControl(this.formGroup());
    this.startTimeErrors.set(errors['startTime'] ?? null);
    this.endTimeErrors.set(errors['endTime'] ?? null);
  }
}
